import React, { useEffect, useState, useMemo, useCallback } from "react";
import Chart from "react-apexcharts"; // npm install react-apexcharts apexcharts
import LoadingSpinner from "../components/dashboard/AdminMetricDashboard/LoadingIndicator/LoadingSpinner";
import { csvDownload } from "../components/dashboard/AdminMetricDashboard/ExportCsv";
import { getJobMetricsCountData } from "../callable-cloud-functions/cloudFunctions";
import toast from "react-hot-toast";
import ToastAlert from "../components/custom-ui/toast-alert/ToastAlert";
import DownloadIconWithLabel from "../components/dashboard/AdminMetricDashboard/DownLoadIcon";

const Last6MonthAggregateData = () => {
  const [rawData, setRawData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getJobMetricsCountData();
      
      console.log("Last 6 months aggregate data:", data);
      
      if (data && Array.isArray(data)) {
        setRawData(data);
      } else {
        setError("Invalid data format received.");
      }
    } catch (err) {
      console.error("Error fetching last 6 months aggregate data:", err);
      setError("Failed to fetch data. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
      fetchData();
      
  }, [fetchData]);

  const chartData = useMemo(() => {
    if (!rawData || rawData.length === 0) return null;

    const months = rawData.map((d) => {
      const date = new Date(d.month + "-01");
      return date.toLocaleString("default", { month: "short", year: "2-digit" });
    });

    const views = rawData.map((d) => d.viewCount);
    const clicks = rawData.map((d) => d.applyCount);

    const options = {
      chart: { type: "bar", toolbar: { show: false } },
      title: {
        text: "Metrics (Job Board)",
        align: "left",
        style: { fontSize: "16px", fontWeight: "600", color: "#183853" },
      },
      plotOptions: {
        bar: { horizontal: false, columnWidth: "50%", borderRadius: 0 },
      },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 2, colors: ["transparent"] },
      xaxis: { categories: months },
      yaxis: { title: { text: "Count" } },
      legend: {
        position: "top",
        horizontalAlign: "right",
        markers: { width: 12, height: 12, radius: 4 },
      },
      fill: { opacity: 1 },
      colors: ["#2b98bd", "#183853"], // Views and Applies
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toString() },
      },
    };

    const series = [
      { name: "Views", data: views },
      { name: "Clicks", data: clicks },
    ];

    return { options, series };
  }, [rawData]);

  /** CSV Export */
  const handleDownload = () => {
    if (!rawData || rawData.length === 0) {
      toast.error("No data available for download.");
      return;
    }
    const finalDataToDownload = rawData.map((job) => ({
      Month: job?.month,
      "Views": job?.viewCount,
      "Clicks": job?.applyCount,
    }));

    csvDownload(finalDataToDownload, "Job_board_metric(last_6_months)");
  };

  return (
    <>
      <div className="mt-2">
        <div className="flex w-full justify-between items-center px-1 mb-4">
          <h1 className="text-xl font-semibold text-instant-teams-blue-Main">
            Job Board Metrics (Last 6 Months)
          </h1>

          <button
            onClick={handleDownload}
            className="bg-instant-teams-blue-Main flex items-center mr-2 text-white px-8 py-2 shadow hover:bg-instant-teams-blue-Dark transition duration-200"
          >
            Download
            <DownloadIconWithLabel isFromEmpMetric={ true} tooltipText={"Export detailed job board metrics covering the past 6 months"}  />
          </button>
        </div>

        <div className="bg-white p-4 rounded-md shadow-lg">
          {loading ? (
            <div className="flex justify-center items-center h-60">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-red-500 text-center h-60 flex items-center justify-center">
              {error}
            </div>
          ) : chartData ? (
            <Chart
              options={chartData.options}
              series={chartData.series}
              type="bar"
              height={350}
            />
          ) : (
            <div className="text-gray-500 text-center h-60 flex items-center justify-center">
              No data available
            </div>
          )}
        </div>
      </div>
      <ToastAlert />
    </>
  );
};

export default Last6MonthAggregateData;
